import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOHeadProps {
  title?: string;
  description?: string;
  path?: string;
  noIndex?: boolean;
}

const defaultDescriptions = {
  de: 'Lerne Deutsch-Vokabeln strukturiert mit GermanPath. Spaced Repetition für nachhaltiges Lernen.',
  en: 'Learn German vocabulary with GermanPath. Spaced repetition for effective learning.',
  tr: 'GermanPath ile Almanca kelimeler öğrenin. Etkili öğrenme için aralıklı tekrar sistemi.',
};

export function SEOHead({ 
  title, 
  description, 
  path = '', 
  noIndex = false 
}: SEOHeadProps) {
  const { language } = useLanguage();
  
  const baseUrl = 'https://germanpath.app';
  const fullUrl = `${baseUrl}${path}`;
  const pageTitle = title ? `${title} | GermanPath` : 'GermanPath – Deutsch Vokabeln lernen';
  const pageDescription = description || defaultDescriptions[language] || defaultDescriptions.de;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', fullUrl);
    }

    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', pageDescription);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', fullUrl);

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', pageTitle);

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', pageDescription);

    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl) twitterUrl.setAttribute('content', fullUrl);

    // Handle noIndex
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (robotsMeta) {
        robotsMeta.setAttribute('content', 'noindex, nofollow');
      }
    } else {
      if (robotsMeta) {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    }
  }, [pageTitle, pageDescription, fullUrl, noIndex]);

  return null;
}
